/** Helpers for Phase 1 Task Scheduling in Task Management. */

import axiosInstance from '../apiServices/axios';
import { buildHierarchyFlatOrder } from './taskHierarchyUtils';

export const SCHEDULING_MODE_SCHEDULING = 'scheduling';

/** Scheduling is always enabled; legacy standard-mode UI branching removed. */
export const isSchedulingMode = _project => true;

/** Phase 1 statuses: Blocked (Stuck is legacy and normalized). */
export const PHASE1_STATUS_OPTIONS = [
    { value: 'COMPLETED', label: 'Completed', bg: 'var(--RAG-Green)', color: 'white' },
    { value: 'WORKING_ON_IT', label: 'Working on it', bg: 'var(--RAG-Amber)', color: 'white' },
    { value: 'BLOCKED', label: 'Blocked', bg: 'var(--RAG-Red)', color: 'white' },
    { value: 'PAUSED', label: 'Paused', bg: '#D5D8E0', color: '#5A5F7A', border: '1px solid #C8CCD6' },
    { value: 'NOT_STARTED', label: 'Not Started', bg: '#FFFFFF', color: '#989BAD', border: '1px solid #DDDEE4' },
    { value: 'CANCELLED', label: 'Cancelled', bg: '#989BAD', color: 'white' },
];

// Back-compat aliases
export const STANDARD_STATUS_OPTIONS = PHASE1_STATUS_OPTIONS;
export const SCHEDULING_STATUS_OPTIONS = PHASE1_STATUS_OPTIONS;

export const getStatusOptionsForProject = _project => PHASE1_STATUS_OPTIONS;

export const normalizeTaskStatus = status => {
    if (!status) return 'NOT_STARTED';
    const code = String(status).trim();
    if (code === 'STUCK') return 'BLOCKED';
    if (!code || code === 'NOT_APPLICABLE' || code.toLowerCase() === 'not applicable') return 'NOT_STARTED';
    return code;
};

/** Display meta for a status code */
export const getStatusDisplayMeta = (status, _project) => {
    const code = normalizeTaskStatus(status);
    return PHASE1_STATUS_OPTIONS.find(o => o.value === code) || PHASE1_STATUS_OPTIONS.find(o => o.value === 'NOT_STARTED');
};

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 480;
export const MINUTES_PER_WEEK = 2400;
export const DURATION_STEP_MINUTES = 15;

export const DURATION_UNITS = [
    { value: 'm', label: 'm', description: 'minutes' },
    { value: 'h', label: 'h', description: 'hours' },
    { value: 'd', label: 'd', description: 'days' },
    { value: 'w', label: 'w', description: 'week' },
];

export const DURATION_SUFFIX_HELP =
    'm = minutes, h = hours, d = days (8h), w = week (Mon–Fri). Mix units if needed, e.g. 1h 15min or 1d 2h 15m. Minimum duration is 15min.';

const quantizeMinutes = (minutes, { roundUp = false } = {}) => {
    if (minutes == null || Number.isNaN(minutes)) return null;
    const value = Math.max(0, Number(minutes));
    if (value < DURATION_STEP_MINUTES) return null;
    const steps = roundUp ? Math.ceil(value / DURATION_STEP_MINUTES) : Math.round(value / DURATION_STEP_MINUTES);
    const quantized = Math.max(steps, 1) * DURATION_STEP_MINUTES;
    return quantized < DURATION_STEP_MINUTES ? null : quantized;
};

export const isSaveableDurationMinutes = minutes => {
    if (minutes == null || minutes === '' || Number.isNaN(Number(minutes))) return false;
    return Number(minutes) >= DURATION_STEP_MINUTES;
};

/** Display duration as 1h, 1h 15min, 1d 2h 15m. */
export const formatDuration = durationMinutes => {
    if (durationMinutes === null || durationMinutes === undefined || durationMinutes === '') return '';
    const minutes = Number(durationMinutes);
    if (Number.isNaN(minutes)) return '';
    const value = Math.max(0, Math.round(minutes));
    if (value === 0) return '0min';

    const weeks = Math.floor(value / MINUTES_PER_WEEK);
    let rem = value % MINUTES_PER_WEEK;
    const days = Math.floor(rem / MINUTES_PER_DAY);
    rem %= MINUTES_PER_DAY;
    const hours = Math.floor(rem / MINUTES_PER_HOUR);
    const mins = rem % MINUTES_PER_HOUR;
    const parts = [];
    if (weeks) parts.push(`${weeks}w`);
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (mins) parts.push(weeks || days ? `${mins}m` : `${mins}min`);
    return parts.join(' ');
};

/** @deprecated use formatDuration — duration is stored in working minutes. */
export const formatDurationDays = formatDuration;

export const parseDurationInput = value => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const text = String(value).trim();
    const tokenRe = /(\d+(?:\.\d+)?)\s*(weeks?|wks?|w|days?|d|hours?|hrs?|h|minutes?|mins?|min|m)\b/gi;
    const tokens = [...text.matchAll(tokenRe)];
    if (tokens.length > 0) {
        let total = 0;
        tokens.forEach(([, amount, unit]) => {
            const num = Number(amount);
            if (Number.isNaN(num)) return;
            const key = String(unit).toLowerCase();
            if (key === 'w' || key.startsWith('week') || key.startsWith('wk')) total += num * MINUTES_PER_WEEK;
            else if (key === 'd' || key.startsWith('day')) total += num * MINUTES_PER_DAY;
            else if (key === 'h' || key.startsWith('hour') || key.startsWith('hr')) total += num * MINUTES_PER_HOUR;
            else total += num;
        });
        return quantizeMinutes(total);
    }

    const num = Number(text);
    if (Number.isNaN(num) || num < 0) return null;
    return quantizeMinutes(num, { roundUp: true });
};

const DURATION_UNIT_SUFFIX_RE = /(\d+(?:\.\d+)?)\s*(weeks?|wks?|w|days?|d|hours?|hrs?|h|minutes?|mins?|min|m)\b/i;

export const draftHasNumber = value => /\d/.test(String(value || ''));

/** Parse input, applying the selected unit when the value is unitless (e.g. 90 + m → 90m). */
export const parseDurationWithDefaultUnit = (value, defaultUnit = 'm') => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const text = String(value).trim();
    if (!draftHasNumber(text)) return null;
    if (DURATION_UNIT_SUFFIX_RE.test(text)) return parseDurationInput(text);
    return parseDurationInput(`${text}${defaultUnit}`);
};

export const getDurationUnitDivisor = unit =>
    unit === 'w' ? MINUTES_PER_WEEK : unit === 'd' ? MINUTES_PER_DAY : unit === 'h' ? MINUTES_PER_HOUR : 1;

/** Express stored minutes as a numeric amount in the given unit (e.g. 7200 → 15 with unit d). */
export const getDurationAmountInUnit = (minutes, unit = 'm') => {
    if (minutes == null || minutes === '' || Number.isNaN(Number(minutes))) return null;
    const amount = Number(minutes) / getDurationUnitDivisor(unit);
    return Number.isInteger(amount) ? amount : Math.round(amount * 100) / 100;
};

/** Parse a numeric amount with an explicit unit (e.g. 15 + d → 15 days in minutes). */
export const parseDurationAmountWithUnit = (amount, unit = 'm') => {
    if (amount == null || amount === '' || Number.isNaN(Number(amount))) return null;
    return parseDurationInput(`${amount}${unit}`);
};

/** Pick the largest unit that yields a whole-number amount for the duration editor (e.g. 4320 → 9d). */
export const inferDurationEditState = (minutes, defaultUnit = 'm') => {
    if (minutes == null || minutes === '' || Number.isNaN(Number(minutes))) {
        return { unit: defaultUnit, amount: '' };
    }

    const value = Math.max(0, Math.round(Number(minutes)));
    if (value === 0) return { unit: defaultUnit, amount: '0' };

    for (const unit of ['w', 'd', 'h', 'm']) {
        const amount = value / getDurationUnitDivisor(unit);
        if (Number.isInteger(amount) && amount > 0) {
            return { unit, amount: String(amount) };
        }
    }

    return { unit: defaultUnit, amount: String(value) };
};

export const applyDurationUnit = (currentMinutes, unit) => {
    if (currentMinutes == null || currentMinutes === '' || Number.isNaN(Number(currentMinutes))) {
        return null;
    }
    const amount = getDurationAmountInUnit(Number(currentMinutes), unit);
    if (amount == null) return null;
    return parseDurationAmountWithUnit(amount, unit);
};

/** True when the task already has one or more subtasks in the list. */
export const taskHasChildren = (task, allTasks = []) => {
    if (!task?.id) return false;
    const parentId = task.id;
    return (allTasks || []).some(
        t => t.parent_task === parentId || t.parent_task_id === parentId || String(t.parent_task) === String(parentId),
    );
};

/** True when a leaf task has timeline / duration / deadline / status the user set. */
export const taskHasManageableScheduleData = task => {
    if (!task) return false;
    const hasTimeline = Boolean(task.start_date || task.end_date);
    const hasDuration = task.duration != null && task.duration !== '';
    const hasDeadline = Boolean(task.deadline);
    const hasCustomStatus = Boolean(task.status && task.status !== 'NOT_STARTED');
    return hasTimeline || hasDuration || hasDeadline || hasCustomStatus;
};

/** First subtask on a leaf with schedule fields — parent values will be cleared / rolled up. */
export const needsSubtaskParentWarning = (parentTask, allTasks = []) => {
    if (!parentTask || taskHasChildren(parentTask, allTasks)) return false;
    return taskHasManageableScheduleData(parentTask);
};

/** Reserved system tag — shown via deadline Off-track pill, not in the Tag column. */
export const OFF_TRACK_TAG = 'Off-track';

export const isReservedOffTrackTag = name => String(name || '').trim().toLowerCase() === 'off-track';

const normalizeTagList = raw => {
    if (Array.isArray(raw) && raw.length > 0) {
        return raw.flatMap(tag => {
            if (tag == null) return [];
            // tag_repr / legacy values may be a single comma-joined string
            return String(tag)
                .split(',')
                .map(part => part.trim())
                .filter(Boolean);
        });
    }
    if (typeof raw === 'string' && raw.trim()) {
        return raw
            .split(',')
            .map(part => part.trim())
            .filter(Boolean);
    }
    return [];
};

/** User-visible tags only (excludes reserved Off-track). */
export const getVisibleTaskTags = task => {
    const raw =
        Array.isArray(task?.tags) && task.tags.length > 0
            ? task.tags
            : task?.tag_repr || [];
    return normalizeTagList(raw).filter(tag => !isReservedOffTrackTag(tag));
};

export const filterVisibleTagNames = (tags = []) =>
    normalizeTagList(tags).filter(tag => !isReservedOffTrackTag(tag));

/** Off-track when End > Deadline (or server tag present). */
export const taskIsOffTrack = task => {
    if (!task) return false;
    const tags = Array.isArray(task.tags) ? task.tags : [];
    if (tags.some(t => isReservedOffTrackTag(t))) return true;
    if (task.end_date && task.deadline) {
        return new Date(task.end_date) > new Date(task.deadline);
    }
    return false;
};

export const taskHasTimeline = task => Boolean(task?.start_date || task?.end_date);

export const taskHasDurationValue = task =>
    task?.duration != null && task.duration !== '';

/**
 * A-02/A-04 confirm only when a later task already has BOTH Duration and Timeline.
 * Duration-only or Timeline-only below does not trigger the modal.
 */
export const hasDownstreamScheduledTasks = (task, allTasks = [], customOrder = null) => {
    if (!task?.id) return false;
    const orderedIds = buildHierarchyFlatOrder(allTasks, customOrder, null);
    const idx = orderedIds.findIndex(id => String(id) === String(task.id));
    if (idx < 0) return false;
    const byId = new Map((allTasks || []).map(t => [String(t.id), t]));
    for (let i = idx + 1; i < orderedIds.length; i += 1) {
        const later = byId.get(String(orderedIds[i]));
        if (later && taskHasDurationValue(later) && taskHasTimeline(later)) return true;
    }
    return false;
};

/** @deprecated use hasDownstreamScheduledTasks */
export const hasDownstreamTimeline = hasDownstreamScheduledTasks;

export const MODE_FS = 'FS';
export const MODE_SS = 'SS';
export const MODE_FF = 'FF';
export const MODE_SF = 'SF';

export const MODE_OPTIONS = [
    {
        value: MODE_FS,
        shortLabel: 'Finish → Start',
        description: 'Start after the previous task finishes',
    },
    {
        value: MODE_SS,
        shortLabel: 'Start → Start',
        description: 'Start when the previous task starts',
    },
    {
        value: MODE_FF,
        shortLabel: 'Finish → Finish',
        description: 'Finish when the previous task finishes',
    },
    {
        value: MODE_SF,
        shortLabel: 'Start → Finish',
        description: 'Finish when the previous task starts',
    },
];

const NON_PARTICIPATING = new Set(['PAUSED', 'CANCELLED']);

/** Leaf eligible for scheduling chain (matches backend). */
export const isEligibleSchedulingLeaf = (task, allTasks = []) => {
    if (!task?.id) return false;
    const hasChildren = (allTasks || []).some(
        t => t.parent_task === task.id || t.parent_task_id === task.id || String(t.parent_task) === String(task.id),
    );
    if (hasChildren) return false;
    if (NON_PARTICIPATING.has(normalizeTaskStatus(task.status))) return false;
    return taskHasDurationValue(task) || taskHasTimeline(task);
};

/** Previous eligible leaf in canonical order. */
export const getEligiblePredecessor = (task, allTasks = [], customOrder = null) => {
    if (!task?.id) return null;
    const orderedIds = buildHierarchyFlatOrder(allTasks, customOrder, null);
    const idx = orderedIds.findIndex(id => String(id) === String(task.id));
    if (idx <= 0) return null;
    const byId = new Map((allTasks || []).map(t => [String(t.id), t]));
    for (let i = idx - 1; i >= 0; i -= 1) {
        const candidate = byId.get(String(orderedIds[i]));
        if (candidate && isEligibleSchedulingLeaf(candidate, allTasks)) {
            return candidate;
        }
    }
    return null;
};

export const getEligiblePredecessorName = (task, allTasks = [], customOrder = null) => {
    const predecessor = getEligiblePredecessor(task, allTasks, customOrder);
    return predecessor?.task || null;
};

/** True when this leaf is the first eligible task in canonical schedule order. */
export const isFirstEligibleSchedulingLeaf = (task, allTasks = [], customOrder = null) => {
    if (!task?.id) return false;
    const orderedIds = buildHierarchyFlatOrder(allTasks, customOrder, null);
    const byId = new Map((allTasks || []).map(t => [String(t.id), t]));
    for (const id of orderedIds) {
        const candidate = byId.get(String(id));
        if (candidate && isEligibleSchedulingLeaf(candidate, allTasks)) {
            return String(candidate.id) === String(task.id);
        }
    }
    return false;
};

export const getModeTooltip = (mode, predecessorName, { showNoPredecessorHint = false } = {}) => {
    const option = MODE_OPTIONS.find(o => o.value === mode) || MODE_OPTIONS[0];
    const lines = [`${option.value} — ${option.shortLabel}`, option.description];
    if (predecessorName) {
        lines.push(`Previous scheduled task: ${predecessorName}`);
    } else if (showNoPredecessorHint) {
        lines.push('This task currently has no predecessor, so Mode does not affect its schedule.');
    } else {
        lines.push('Mode applies to schedulable leaf tasks.');
    }
    return lines.join('\n');
};

/** Server-side dry-run for schedule mutations. */
export const fetchSchedulePreview = async (taskId, mutation = {}, flags = {}) => {
    const projectId = localStorage.getItem('project_id');
    const { data } = await axiosInstance.post(
        `${'/tools/tasks/'}${projectId}/${taskId}/schedule_preview/`,
        {
            mutation,
            update_only: flags.updateOnly ?? false,
            adjust_downstream_deadlines: flags.adjustDownstreamDeadlines ?? false,
            adjust_timeline_to_deadline: flags.adjustTimelineToDeadline ?? false,
        },
    );
    return data;
};

export const fetchDeletePreview = async taskId => {
    const projectId = localStorage.getItem('project_id');
    const { data } = await axiosInstance.post(
        `${'/tools/tasks/'}${projectId}/${taskId}/delete_preview/`,
    );
    return data;
};

export const fetchReorderPreview = async taskOrders => {
    const projectId = localStorage.getItem('project_id');
    const { data } = await axiosInstance.post(
        `${'/tools/tasks/'}${projectId}/reorder_preview/`,
        { task_orders: taskOrders },
    );
    return data;
};

export const fetchCreatePreview = async payload => {
    const projectId = localStorage.getItem('project_id');
    const { data } = await axiosInstance.post(`${'/tools/tasks/'}${projectId}/create_preview/`, payload);
    return data;
};

export const fetchBulkSchedulePreview = async (taskIds, mutation, flags = {}) => {
    const projectId = localStorage.getItem('project_id');
    const { data } = await axiosInstance.post(`${'/tools/tasks/'}${projectId}/bulk_schedule_preview/`, {
        task_ids: taskIds,
        mutation,
        update_only: flags.updateOnly ?? false,
        adjust_downstream_deadlines: flags.adjustDownstreamDeadlines ?? false,
        adjust_timeline_to_deadline: flags.adjustTimelineToDeadline ?? false,
    });
    return data;
};

export const taskPayloadHasScheduleInput = payload =>
    Boolean(
        payload?.start_date ||
            payload?.end_date ||
            payload?.duration ||
            payload?.duration_minutes,
    );

export const wouldAffectOtherTasks = preview => Boolean(preview?.would_affect_others);
